import { useMemo, useState } from "react"
import classNames from "classnames"
import { ButtonPrimary, ButtonSecondary } from "@nova/component-btn"
import { CopyButton } from "@nova/component-copy-button"
import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { config, getApiGatewayDomainForRegion } from "@nova/configuration/browser"
import { prettyRegionName, SupportedRegions } from "@nova/constants"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useFeatureFlag } from "features/feature-flags/useFeatureFlag"
import { API_ROUTE_CATALOG, ApiRoute, ApiRouteGroup, pathParamNames } from "./apiRouteCatalog"

const ROUTE_GROUPS: ApiRouteGroup[] = [
    "Authentication",
    "Workspace Management",
    "Data Ingest",
    "Product Retrieval",
]

// Mirrors the allowed-regions gating in features/modals/OrganizationEditorModal.tsx
const BETA_REGIONS: SupportedRegions[] = [SupportedRegions.Australia]

function buildUrl(route: ApiRoute, host: string, values: Record<string, string>): string {
    let path = route.path
    for (const name of pathParamNames(route.path)) {
        path = path.replace(`:${name}`, encodeURIComponent(values[name] || `:${name}`))
    }
    const query = new URLSearchParams()
    for (const q of route.query ?? []) {
        const value = values[`q_${q.name}`]
        if (value) query.set(q.name, value)
    }
    const qs = query.toString()
    return `${host}${path}${qs ? `?${qs}` : ""}`
}

function buildBody(route: ApiRoute, values: Record<string, string>): string | undefined {
    if (!route.body?.length) return undefined
    const body: Record<string, string> = {}
    for (const field of route.body) {
        const value = values[`b_${field.name}`]
        if (value) body[field.name] = value
    }
    return Object.keys(body).length ? JSON.stringify(body) : undefined
}

function buildCurl(route: ApiRoute, url: string, token: string, values: Record<string, string>) {
    const lines = [`curl -X ${route.method} "${url}"`, `  -H "Authorization: Bearer ${token}"`]
    const body = buildBody(route, values)
    if (body) {
        lines.push(`  -H "Content-Type: application/json"`)
        lines.push(`  -d '${body}'`)
    }
    return lines.join(" \\\n")
}

type SentResponse = {
    ok: boolean
    status: number
    statusText: string
    durationMs: number
    bodyText: string
}

function formatJsonSafely(rawText: string): string {
    try {
        return JSON.stringify(JSON.parse(rawText), null, 2)
    } catch {
        return rawText
    }
}

export function ApiExploreTabContent() {
    const { organizationSettingRegion } = config.use("organizationSettingRegion")
    const [region, setRegion] = useState<SupportedRegions>(
        organizationSettingRegion as SupportedRegions
    )

    const orgId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const betaRegionsEnabled = useFeatureFlag("betaRegions")
    const allowedRegions = Object.values(SupportedRegions).filter(
        (r) => betaRegionsEnabled || !BETA_REGIONS.includes(r)
    )

    const [activeRouteId, setActiveRouteId] = useState(API_ROUTE_CATALOG[0].id)
    const [paramValues, setParamValues] = useState<Record<string, string>>({ orgId })
    const [cachedToken, setCachedToken] = useState<string | null>(null)
    const [isMintingToken, setIsMintingToken] = useState(false)
    const [justCopiedCurl, setJustCopiedCurl] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [response, setResponse] = useState<SentResponse | null>(null)

    const activeRoute = useMemo(
        () => API_ROUTE_CATALOG.find((route) => route.id === activeRouteId) ?? API_ROUTE_CATALOG[0],
        [activeRouteId]
    )
    const host = getApiGatewayDomainForRegion(region)
    const builtUrl = useMemo(
        () => buildUrl(activeRoute, host, paramValues),
        [activeRoute, host, paramValues]
    )
    const pathParams = useMemo(() => pathParamNames(activeRoute.path), [activeRoute])
    const builtBody = useMemo(() => buildBody(activeRoute, paramValues), [activeRoute, paramValues])
    const bodyPreview = useMemo(
        () => (builtBody ? JSON.stringify(JSON.parse(builtBody), null, 2) : undefined),
        [builtBody]
    )

    const setParam = (name: string, value: string) =>
        setParamValues((prev) => ({ ...prev, [name]: value }))

    const getToken = async () => {
        if (cachedToken) return cachedToken
        setIsMintingToken(true)
        try {
            const { token } = await fetchApiGateway("post /me/api-token")
            setCachedToken(token)
            return token
        } finally {
            setIsMintingToken(false)
        }
    }

    const handleCopyCurl = async () => {
        const token = await getToken()
        await navigator.clipboard.writeText(buildCurl(activeRoute, builtUrl, token, paramValues))
        setJustCopiedCurl(true)
        setTimeout(() => setJustCopiedCurl(false), 3000)
    }

    const handleSend = async () => {
        setIsSending(true)
        setResponse(null)
        const startedAt = performance.now()
        try {
            const token = await getToken()
            const body = buildBody(activeRoute, paramValues)
            const res = await fetch(builtUrl, {
                method: activeRoute.method,
                headers: {
                    Authorization: `Bearer ${token}`,
                    ...(body ? { "Content-Type": "application/json" } : {}),
                },
                body,
            })
            const durationMs = Math.round(performance.now() - startedAt)
            const contentType = res.headers.get("content-type") || ""
            const rawText = await res.text()
            const bodyText = contentType.includes("application/json")
                ? formatJsonSafely(rawText)
                : rawText
            setResponse({
                ok: res.ok,
                status: res.status,
                statusText: res.statusText,
                durationMs,
                bodyText,
            })
        } catch (err) {
            setResponse({
                ok: false,
                status: 0,
                statusText: "Request failed",
                durationMs: Math.round(performance.now() - startedAt),
                bodyText: String(err),
            })
        } finally {
            setIsSending(false)
        }
    }

    return (
        <div className="flex flex-col gap-4 py-6 px-2 h-full">
            <div className="flex gap-6 flex-1 min-h-0">
                <div className="flex flex-col gap-4 w-[220px] shrink-0 overflow-y-auto pr-2 border-r border-white-100">
                    <div className="flex flex-col w-full pb-2 gap-2">
                        <div className="flex items-center gap-2">
                            <Icons.SmallIcon Icon={Icons.Globe} className="text-gray-300" />
                            <span className="text-[14px] text-gray-300">Region</span>
                        </div>
                        <Dropdown>
                            <Dropdown.Trigger>
                                <ButtonSecondary
                                    title={prettyRegionName[region]}
                                    RightIcon={Icons.ArrowDropdown}
                                    className="w-full !justify-between"
                                />
                            </Dropdown.Trigger>
                            <Dropdown.Menu position="bottom-left">
                                {allowedRegions.map((r) => (
                                    <Dropdown.ButtonItem key={r} onClick={() => setRegion(r)}>
                                        {prettyRegionName[r]}
                                    </Dropdown.ButtonItem>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    {ROUTE_GROUPS.map((group) => {
                        const routes = API_ROUTE_CATALOG.filter((route) => route.group === group)
                        if (!routes.length) return null
                        return (
                            <div key={group}>
                                <small className="block xs pb-2 text-gray-300">{group}</small>
                                {routes.map((route) => (
                                    <button
                                        key={route.id}
                                        type="button"
                                        onClick={() => {
                                            setActiveRouteId(route.id)
                                            setResponse(null)
                                        }}
                                        className={classNames(
                                            "w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px]",
                                            route.id === activeRoute.id
                                                ? "bg-white-100 text-gray-0"
                                                : "text-gray-100 hover:bg-white-100"
                                        )}
                                    >
                                        <span
                                            className={classNames(
                                                "font-mono w-12 text-[11px] text-center font-bold px-1.5 pt-1 pb-0.5 rounded-md shrink-0",
                                                route.method === "GET"
                                                    ? "border border-green-800"
                                                    : "border border-yellow-800"
                                            )}
                                        >
                                            {route.method}
                                        </span>
                                        <span className="truncate">{route.label}</span>
                                    </button>
                                ))}
                            </div>
                        )
                    })}
                </div>

                <div className="flex flex-col gap-4 flex-1 min-w-0 overflow-y-auto pb-6">
                    <h2 className='w-full pb-4 font-["PP_Telegraf"]'>{activeRoute.label}</h2>
                    <div>
                        <div className="font-mono text-[14px] text-gray-0 break-all">
                            {activeRoute.method} {activeRoute.path}
                        </div>
                        <div className="text-[14px] text-gray-300 mt-1.5">
                            {activeRoute.summary}
                        </div>
                    </div>

                    {pathParams.length > 0 && (
                        <ParamSection
                            title="Path parameters"
                            params={pathParams.map((name) => ({ name, required: true }))}
                            prefix=""
                            values={paramValues}
                            onChange={setParam}
                        />
                    )}
                    {!!activeRoute.query?.length && (
                        <ParamSection
                            title="Query parameters (optional)"
                            params={activeRoute.query}
                            prefix="q_"
                            values={paramValues}
                            onChange={setParam}
                        />
                    )}
                    {!!activeRoute.body?.length && (
                        <ParamSection
                            title="Body"
                            params={activeRoute.body}
                            prefix="b_"
                            values={paramValues}
                            onChange={setParam}
                        />
                    )}

                    <div>
                        <div className="text-[11px] uppercase tracking-wide text-gray-300 mb-1">
                            Request URL
                        </div>
                        <div className="flex items-center gap-2 bg-gray-800 border border-gray-700 rounded p-3">
                            <code className="flex-1 text-[12px] text-gray-0 break-all">
                                {builtUrl}
                            </code>
                            <CopyButton value={builtUrl} />
                        </div>
                    </div>

                    {!!activeRoute.body?.length && (
                        <div>
                            <div className="text-[11px] uppercase tracking-wide text-gray-300 mb-1">
                                Request Body
                            </div>
                            <div className="flex items-start gap-2 bg-gray-800 border border-gray-700 rounded p-3">
                                <pre className="flex-1 text-[12px] text-gray-0 whitespace-pre-wrap break-all">
                                    {bodyPreview ?? "{}"}
                                </pre>
                                <CopyButton value={bodyPreview ?? "{}"} className="shrink-0" />
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-4">
                        <ButtonSecondary
                            title={
                                justCopiedCurl
                                    ? "Copied!"
                                    : isMintingToken
                                      ? "Minting token..."
                                      : "Copy as curl"
                            }
                            LeftIcon={Icons.Terminal}
                            onClick={handleCopyCurl}
                            disabled={isMintingToken}
                            className="w-36"
                        />
                        <ButtonPrimary
                            title={isSending ? "Sending..." : "Send Request"}
                            LeftIcon={Icons.Play}
                            onClick={handleSend}
                            disabled={isSending}
                            className="w-36"
                        />
                    </div>

                    {response && (
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <div className="text-[11px] uppercase tracking-wide text-gray-300">
                                    Response
                                </div>
                                <div
                                    className={classNames(
                                        "text-[12px] font-mono",
                                        response.ok ? "text-green-500" : "text-red-500"
                                    )}
                                >
                                    {response.status} {response.statusText} · {response.durationMs}
                                    ms
                                </div>
                            </div>
                            <div className="flex items-start gap-2 bg-gray-800 border border-gray-700 rounded p-3 max-h-[280px] overflow-auto">
                                <pre className="flex-1 text-[12px] text-gray-0 whitespace-pre-wrap break-all">
                                    {response.bodyText}
                                </pre>
                                <CopyButton value={response.bodyText} className="shrink-0" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

function ParamSection({
    title,
    params,
    prefix,
    values,
    onChange,
}: {
    title: string
    params: { name: string; placeholder?: string; required?: boolean }[]
    prefix: string
    values: Record<string, string>
    onChange: (name: string, value: string) => void
}) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-300 mb-2">{title}</div>
            <div className="flex flex-col gap-2">
                {params.map((param) => {
                    const key = `${prefix}${param.name}`
                    return (
                        <div key={key} className="flex items-center gap-3">
                            <label
                                htmlFor={`api-explore-${key}`}
                                className="min-w-[110px] font-mono text-[12px] text-gray-100"
                            >
                                {param.name}
                                {param.required && " *"}
                            </label>
                            <input
                                id={`api-explore-${key}`}
                                type="text"
                                value={values[key] ?? ""}
                                placeholder={param.placeholder}
                                onChange={(e) => onChange(key, e.target.value)}
                                className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-[12px] font-mono text-gray-0 placeholder:text-gray-400 focus:outline-none focus:border-blue-500"
                            />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
